/**
 * Client-side session handling for THE BUILT INDEX™.
 *
 * Answers live in localStorage until the final step so a refresh never loses
 * progress; scoring happens here and the results are posted to the
 * `built-index` edge function, which owns persistence and the AI letter.
 */

import { supabase } from "@/integrations/supabase/client";
import { attributionProps } from "@/lib/utm";
import {
  BUILT_PRINCIPLES,
  DISC_PROFILES,
  DISC_QUESTIONS,
  LOVE_LANGUAGES,
  LOVE_PROFILES,
  LOVE_QUESTIONS,
  LOVIN_DIMENSIONS,
  calculateBuiltTotal,
  calculateDiscType,
  calculateLoveProfile,
  calculateLovinScore,
  getBuiltTier,
  getDominantPrinciple,
  getWeakestPrinciple,
  type BuiltKey,
  type DiscType,
  type LoveLanguageKey,
  type LovinKey,
} from "@/content/built-index";

const STORAGE_KEY = "built_index_session_v1";

export type BuiltIndexState = {
  id: string | null;
  token?: string | null;
  fullName: string;
  email: string;
  built: Partial<Record<BuiltKey, number>>;
  disc: Record<string, string>;
  love: Record<string, string>;
  lovin: Partial<Record<LovinKey, number>>;
};

const EMPTY: BuiltIndexState = {
  id: null,
  fullName: "",
  email: "",
  built: {},
  disc: {},
  love: {},
  lovin: {},
};

export function readSession(): BuiltIndexState {
  if (typeof window === "undefined") return { ...EMPTY };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    return { ...EMPTY, ...(JSON.parse(raw) as Partial<BuiltIndexState>) };
  } catch {
    return { ...EMPTY };
  }
}

export function writeSession(patch: Partial<BuiltIndexState>): BuiltIndexState {
  const next = { ...readSession(), ...patch };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — in-memory only */
  }
  return next;
}

export function clearSession() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/* -------------------------------------------------------------------------- */
/* Scoring                                                                     */
/* -------------------------------------------------------------------------- */

export function scoreSession(state: BuiltIndexState) {
  const builtTotal = calculateBuiltTotal(state.built);
  const builtTier = getBuiltTier(builtTotal);
  const disc = calculateDiscType(state.disc);
  const love = calculateLoveProfile(state.love);
  const lovin = calculateLovinScore(state.lovin);

  const label = (key: LoveLanguageKey) =>
    LOVE_LANGUAGES.find((l) => l.key === key)?.name ?? key;

  return {
    builtTotal,
    builtTier: builtTier.tier,
    builtTierLabel: builtTier.label,
    strongest: getDominantPrinciple(state.built),
    weakest: getWeakestPrinciple(state.built),
    discType: disc.type as DiscType,
    discScores: disc.scores,
    discName: DISC_PROFILES[disc.type].name,
    loveFuel: love.primaryFuel,
    loveDrain: love.primaryDrain,
    loveFuelLabel: label(love.primaryFuel),
    loveDrainLabel: label(love.primaryDrain),
    loveBooklet: LOVE_PROFILES[love.primaryFuel].booklet,
    lovinScore: lovin.score,
    lovinTier: lovin.tier,
    lovinDescription: lovin.description,
  };
}

export type BuiltIndexScores = ReturnType<typeof scoreSession>;

export const isBuiltComplete = (s: BuiltIndexState) =>
  BUILT_PRINCIPLES.every((p) => typeof s.built[p.key] === "number");
export const isDiscComplete = (s: BuiltIndexState) =>
  DISC_QUESTIONS.every((q) => Boolean(s.disc[q.id]));
export const isLoveComplete = (s: BuiltIndexState) =>
  LOVE_QUESTIONS.every((q) => Boolean(s.love[q.id]));
export const isLovinComplete = (s: BuiltIndexState) =>
  LOVIN_DIMENSIONS.every((d) => typeof s.lovin[d.key] === "number");

/* -------------------------------------------------------------------------- */
/* API                                                                         */
/* -------------------------------------------------------------------------- */

async function call<T>(body: Record<string, unknown>): Promise<T> {
  if (body.action !== "start") body = { ...body, token: readSession().token ?? null };
  const { data, error } = await supabase.functions.invoke("built-index", { body });
  if (error) {
    const message =
      (data as { error?: string } | null)?.error ??
      error.message ??
      "Something went wrong. Please try again.";
    throw new Error(message);
  }
  if ((data as { error?: string })?.error) {
    throw new Error((data as { error: string }).error);
  }
  return data as T;
}

export async function startBuiltIndex(input: {
  fullName: string;
  email: string;
  phone?: string;
  role?: string;
}): Promise<string> {
  const { id, token } = await call<{ id: string; token: string }>({
    action: "start",
    ...input,
    source: "built-index",
    page: typeof window === "undefined" ? undefined : window.location.pathname,
    attribution: attributionProps(),
  });
  writeSession({ id, token, fullName: input.fullName, email: input.email });
  return id;
}

export async function submitBuiltIndex(state: BuiltIndexState) {
  if (!state.id) throw new Error("Start the assessment before submitting.");
  const results = scoreSession(state);
  return call<{ id: string; builtLetter: string }>({
    action: "submit",
    id: state.id,
    builtScores: state.built,
    discAnswers: state.disc,
    loveAnswers: state.love,
    lovinAnswers: state.lovin,
    results,
  });
}

export type BuiltIndexRecord = {
  id: string;
  fullName: string;
  role: string | null;
  completed: boolean;
  paid: boolean;
  builtTotal: number | null;
  builtTier: string | null;
  discType: DiscType | null;
  loveFuel: LoveLanguageKey | null;
  loveDrain: LoveLanguageKey | null;
  lovinScore: number | null;
  lovinTier: string | null;
  builtLetter: string | null;
  builtScores?: Partial<Record<BuiltKey, number>> | null;
  discScores?: Record<DiscType, number> | null;
  lovinAnswers?: Partial<Record<LovinKey, number>> | null;
  loveAnswers?: Record<string, string> | null;
};

export const getBuiltIndexRecord = (id: string) =>
  call<BuiltIndexRecord>({ action: "get", id });

export const createBuiltIndexCheckout = (input: {
  id: string;
  returnUrl: string;
  environment: "sandbox" | "live";
}) => call<{ clientSecret: string }>({ action: "checkout", ...input });

export const unlockBuiltIndex = (input: {
  id: string;
  sessionId: string;
  environment: "sandbox" | "live";
}) => call<{ paid: boolean }>({ action: "unlock", ...input });
